import React, { createContext, useContext, useState, useEffect } from 'react';
import { Atividade } from '../types';
import { useAuth } from './AuthContext';
import atividadeService from '../services/atividadeService';
import pollingService from '../services/PollingService';
import { toast } from 'react-toastify';

interface AtividadeContextType {
  atividades: Atividade[];
  atividadesNaoLidas: number;
  carregando: boolean;
  erro: string;
  atualizacoesTempoReal: boolean;
  obterAtividadesPorTarefa: (tarefaId: string) => Promise<Atividade[]>;
  marcarComoLida: (atividadeId: string) => Promise<boolean>;
  marcarTodasComoLidas: () => Promise<boolean>;
  carregarMaisAtividades: () => Promise<boolean>;
  toggleAtualizacoesTempoReal: () => void;
}

const AtividadeContext = createContext<AtividadeContextType | undefined>(undefined);

export const AtividadeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string>('');
  const [paginaAtual, setPaginaAtual] = useState<number>(1);
  const [totalPaginas, setTotalPaginas] = useState<number>(1);
  const [atualizacoesTempoReal, setAtualizacoesTempoReal] = useState<boolean>(true);
  const { usuario } = useAuth();

  // Calcular número de atividades não lidas
  const atividadesNaoLidas = atividades.filter(a => !a.lida).length;

  // Função para iniciar o polling
  const iniciarPolling = () => {
    if (!usuario) return;
    pollingService.iniciarPollingFuncao(
      'atividades',
      () => carregarAtividades(1),
      180000 // 3 minutos
    );
  };

  // Função para parar o polling
  const pararPolling = () => {
    pollingService.pararPolling('atividades');
  };
  
  // Função para alternar o estado das atualizações em tempo real
  const toggleAtualizacoesTempoReal = () => {
    const novoEstado = !atualizacoesTempoReal;
    setAtualizacoesTempoReal(novoEstado);
    
    if (novoEstado) {
      // Reativar o polling
      iniciarPolling();
      toast.success('Atualizações em tempo real ativadas');
    } else {
      // Desativar o polling
      pararPolling();
      toast.info('Atualizações em tempo real desativadas');
    }
  };
  
  // Efeito para iniciar/parar o polling quando o estado muda ou o componente é montado/desmontado
  useEffect(() => {
    if (atualizacoesTempoReal && usuario) {
      iniciarPolling();
    } else {
      pararPolling();
    }
    
    return () => {
      pararPolling();
    };
  }, [atualizacoesTempoReal, usuario?.id]);

  // Carregar atividades quando o usuário estiver autenticado
  useEffect(() => {
    if (usuario) {
      carregarAtividades();
    }
  }, [usuario]);

  // Função para carregar atividades do usuário
  const carregarAtividades = async (pagina: number = 1, limite: number = 20) => {
    if (!usuario) return;
    
    setCarregando(true);
    try {
      const resposta = await atividadeService.obterTodas(
        {}, // Sem filtros específicos
        { pagina, limite }
      );
      
      if (resposta.success && resposta.atividades) {
        const novasAtividades = resposta.atividades || [];
        if (pagina === 1) {
          setAtividades(novasAtividades);
        } else {
          setAtividades(prev => [...prev, ...novasAtividades]);
        }
        
        if (resposta.paginacao) {
          setPaginaAtual(resposta.paginacao.pagina);
          setTotalPaginas(resposta.paginacao.paginas);
        }
        
        setErro('');
      } else {
        throw new Error(resposta.message || 'Erro ao carregar atividades');
      }
    } catch (error: any) {
      setErro(error.message || 'Erro ao carregar atividades');
      console.error('Erro ao carregar atividades:', error);
    } finally {
      setCarregando(false);
    }
  };

  // Função para carregar mais atividades (paginação)
  const carregarMaisAtividades = async (): Promise<boolean> => {
    if (paginaAtual >= totalPaginas || carregando) return false;
    
    const proximaPagina = paginaAtual + 1;
    await carregarAtividades(proximaPagina);
    return true;
  };

  // Função para obter atividades de uma tarefa específica
  const obterAtividadesPorTarefa = async (tarefaId: string): Promise<Atividade[]> => {
    try {
      const resposta = await atividadeService.obterPorTarefa(tarefaId);
      if (resposta.success && resposta.atividades) {
        return resposta.atividades;
      }
      return [];
    } catch (error) {
      console.error(`Erro ao obter atividades da tarefa ${tarefaId}:`, error);
      return [];
    }
  };

  // Função para marcar uma atividade como lida
  const marcarComoLida = async (atividadeId: string): Promise<boolean> => {
    try {
      // Verificar se o usuário atual é o destinatário da atividade
      const atividade = atividades.find(a => a.id === atividadeId);
      
      if (!atividade) {
        console.error(`Atividade ${atividadeId} não encontrada`);
        return false;
      }
      
      const resposta = await atividadeService.marcarComoLida(atividadeId);
      if (resposta.success) {
        // Atualizar o estado local
        setAtividades(prev => 
          prev.map(ativ => 
            ativ.id === atividadeId ? { ...ativ, lida: true } : ativ
          )
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Erro ao marcar atividade ${atividadeId} como lida:`, error);
      return false;
    }
  };

  // Função para marcar todas as atividades como lidas
  const marcarTodasComoLidas = async (): Promise<boolean> => {
    try {
      // Filtrar TODAS as atividades não lidas (não importa quem criou)
      const atividadesNaoLidas = atividades.filter(a => !a.lida);
      
      const idsParaMarcar = atividadesNaoLidas.map(a => a.id);

      if (idsParaMarcar.length === 0) {
        toast.info('Não há atividades pendentes para marcar como lidas');
        return true;
      }

      const resposta = await atividadeService.marcarMultiplasComoLidas(idsParaMarcar);

      if (resposta.success) {
        setAtividades(prev =>
          prev.map(ativ =>
            idsParaMarcar.includes(ativ.id) ? { ...ativ, lida: true } : ativ
          )
        );
        toast.success(`${idsParaMarcar.length} atividade(s) marcada(s) como lida(s)`);
        
        return true;
      } else {
        toast.error(resposta.message || 'Erro ao marcar atividades como lidas');
        return false;
      }
    } catch (error) {
      console.error('Erro ao marcar atividades como lidas:', error);
      toast.error('Erro ao marcar atividades como lidas');
      return false;
    }
  };

  return (
    <AtividadeContext.Provider
      value={{
        atividades,
        atividadesNaoLidas,
        carregando,
        erro,
        atualizacoesTempoReal,
        obterAtividadesPorTarefa,
        marcarComoLida,
        marcarTodasComoLidas,
        carregarMaisAtividades,
        toggleAtualizacoesTempoReal
      }}
    >
      {children}
    </AtividadeContext.Provider>
  );
};

export const useAtividade = () => {
  const context = useContext(AtividadeContext);
  if (context === undefined) {
    throw new Error('useAtividade deve ser usado dentro de um AtividadeProvider');
  }
  return context;
};
